/* MAISON — theme.js
   Lightweight vanilla JS for cart drawer, search, accordions, scroll reveals, etc.
*/
(function () {
  'use strict';

  // ---------- Utilities ----------
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const on = (el, evt, fn) => el && el.addEventListener(evt, fn);

  function lockScroll(lock) {
    document.body.classList.toggle('lock-scroll', lock);
  }

  function toast(msg) {
    const root = $('#ToastRoot');
    if (!root) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = msg;
    root.appendChild(el);
    setTimeout(() => el.remove(), 2800);
  }

  function formatMoney(cents, currency = 'EUR') {
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }
  window.maisonFormatMoney = formatMoney;

  // ---------- Sticky header ----------
  const header = $('.header');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ---------- Mobile menu ----------
  const mobileMenu = $('#MobileMenu');
  $$('[data-toggle="mobile-menu"]').forEach(btn => {
    on(btn, 'click', () => {
      if (!mobileMenu) return;
      const open = mobileMenu.classList.toggle('is-open');
      lockScroll(open);
    });
  });

  // ---------- Drawer overlay (shared) ----------
  function ensureOverlay() {
    let ov = $('.drawer-overlay');
    if (!ov) {
      ov = document.createElement('div');
      ov.className = 'drawer-overlay';
      document.body.appendChild(ov);
      ov.addEventListener('click', closeAll);
    }
    return ov;
  }

  function openDrawer(el) {
    if (!el) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    ensureOverlay().classList.add('is-active');
    lockScroll(true);
    trapFocus(el);
  }

  function closeAll() {
    $$('.cart-drawer, .search-drawer, .filter-drawer, .mobile-menu').forEach(d => {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    });
    const ov = $('.drawer-overlay');
    if (ov) ov.classList.remove('is-active');
    lockScroll(false);
    releaseFocus();
  }

  // ---------- Focus trap (a11y) ----------
  let _trappedEl = null;
  let _lastFocused = null;
  function getFocusable(el) {
    return $$('button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', el);
  }
  function trapFocus(el) {
    _trappedEl = el;
    _lastFocused = document.activeElement;
    const focusables = getFocusable(el);
    if (focusables.length) {
      requestAnimationFrame(() => focusables[0].focus());
    }
  }
  function releaseFocus() {
    _trappedEl = null;
    if (_lastFocused && typeof _lastFocused.focus === 'function') {
      requestAnimationFrame(() => _lastFocused.focus());
      _lastFocused = null;
    }
  }
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      closeAll();
      // Also close any open dialog
      $$('dialog[open]').forEach(d => { if (d.close) d.close(); });
      return;
    }
    if (e.key !== 'Tab' || !_trappedEl) return;
    const focusables = getFocusable(_trappedEl);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  // Trap focus inside open <dialog> elements too
  document.addEventListener('keydown', e => {
    if (e.key !== 'Tab') return;
    const openDialog = $('dialog[open]');
    if (!openDialog) return;
    const focusables = getFocusable(openDialog);
    if (!focusables.length) return;
    const first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  // ---------- Cart drawer ----------
  const cartDrawer = $('#CartDrawer');

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  function updateCartCount(count) {
    $$('.header__cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    try {
      const res = await fetch((window.routes?.cart_url || '/cart') + '?section_id=cart-drawer');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('#CartDrawer .cart-drawer__contents');
      const target = cartDrawer.querySelector('.cart-drawer__contents');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
      bindCartItemEvents();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      // fallback: just refresh count
      const c = await fetchCart();
      updateCartCount(c.item_count);
    }
  }

  function bindCartItemEvents() {
    $$('[data-cart-change]').forEach(btn => {
      on(btn, 'click', async () => {
        const line = +btn.dataset.line;
        const qty = +btn.dataset.qty;
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ line, quantity: qty })
          });
          const cart = await fetchCart();
          updateCartCount(cart.item_count);
          refreshCartDrawer();
        } catch (e) {
          toast('Could not update');
          console.error(e);
        }
      });
    });
  }

  $$('[data-open-cart]').forEach(btn => {
    on(btn, 'click', e => {
      e.preventDefault();
      openDrawer(cartDrawer);
    });
  });

  $$('[data-close-cart]').forEach(btn => on(btn, 'click', closeAll));

  // ---------- Add to cart (intercept product forms) ----------
  $$('form[data-product-form], #ProductForm').forEach(form => {
    on(form, 'submit', async e => {
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      const formData = new FormData(form);
      btn?.setAttribute('aria-disabled', 'true');
      const original = btn?.textContent;
      if (btn) btn.textContent = '…';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        if (!res.ok) {
          const err = await res.json();
          toast(err.description || err.message || 'Could not add to cart');
          return;
        }
        const cart = await fetchCart();
        updateCartCount(cart.item_count);
        await refreshCartDrawer();
        openDrawer(cartDrawer);
      } catch (err) {
        toast('Could not add to cart');
      } finally {
        btn?.removeAttribute('aria-disabled');
        if (btn && original) btn.textContent = original;
      }
    });
  });

  bindCartItemEvents();

  // ---------- Product page: option select ----------
  $$('[data-product-options]').forEach(root => {
    const variantsData = root.dataset.variants;
    if (!variantsData) return;
    let variants;
    try { variants = JSON.parse(variantsData); } catch { return; }

    const optionInputs = $$('input[data-option-input]', root);
    const priceEl = $('[data-product-price]');
    const compareEl = $('[data-product-compare]');
    const formInput = $('input[name="id"]', root.closest('form') || document);
    const atcBtn = $('[data-atc]', root.closest('form') || document);

    function selected() {
      const selectedOpts = [];
      const optionNames = new Set(optionInputs.map(i => i.name));
      optionNames.forEach(name => {
        const checked = optionInputs.find(i => i.name === name && i.checked);
        if (checked) selectedOpts.push(checked.value);
      });
      return selectedOpts;
    }

    function updateOptionLabels() {
      $$('.product__option', root).forEach(optEl => {
        const display = optEl.querySelector('[data-option-display]');
        const checked = optEl.querySelector('input[data-option-input]:checked');
        if (display && checked) display.textContent = checked.value;
      });
    }

    function update() {
      const opts = selected();
      // Toujours refléter la valeur choisie (couleur, taille…) dans le libellé
      updateOptionLabels();
      const match = variants.find(v => v.options.every((o, i) => o === opts[i]));
      if (!match) {
        if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = atcBtn.dataset.unavailableText || 'Unavailable'; }
        return;
      }
      if (formInput) formInput.value = match.id;
      if (priceEl) priceEl.textContent = formatMoney(match.price, window.Shopify?.currency?.active || 'EUR');
      if (compareEl) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          compareEl.textContent = formatMoney(match.compare_at_price, window.Shopify?.currency?.active || 'EUR');
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
      if (atcBtn) {
        atcBtn.disabled = !match.available;
        atcBtn.textContent = match.available ? (atcBtn.dataset.addText || 'Add to bag') : (atcBtn.dataset.soldoutText || 'Sold out');
      }
      // Highlight active swatch / size button
      $$('[data-option-input]', root).forEach(i => {
        const label = i.closest('label, .opt-color__btn, .opt-size__btn');
        if (label) label.classList.toggle('is-active', i.checked);
      });
    }

    optionInputs.forEach(i => on(i, 'change', update));
    update();
  });

  // ---------- Quantity stepper ----------
  $$('.qty').forEach(qty => {
    const input = qty.querySelector('input');
    qty.querySelectorAll('button').forEach(btn => {
      on(btn, 'click', () => {
        const dir = btn.dataset.dir === 'down' ? -1 : 1;
        const next = Math.max(1, (+input.value || 1) + dir);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  // ---------- Accordions ----------
  $$('.acc__item').forEach(item => {
    const btn = item.querySelector('.acc__btn');
    on(btn, 'click', () => item.classList.toggle('is-open'));
  });

  // ---------- Predictive search ----------
  const searchDrawer = $('#SearchDrawer');
  $$('[data-open-search]').forEach(btn => on(btn, 'click', () => {
    openDrawer(searchDrawer);
    setTimeout(() => searchDrawer?.querySelector('input')?.focus(), 50);
  }));
  $$('[data-close-search]').forEach(btn => on(btn, 'click', closeAll));

  const searchInput = $('#PredictiveSearchInput');
  const searchResults = $('#PredictiveSearchResults');
  let searchTimer;
  on(searchInput, 'input', e => {
    const q = e.target.value.trim();
    clearTimeout(searchTimer);
    if (q.length < 2) { if (searchResults) searchResults.innerHTML = ''; return; }
    searchTimer = setTimeout(async () => {
      try {
        const res = await fetch(`/search/suggest?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&section_id=predictive-search`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('#PredictiveSearchResults');
        if (fresh && searchResults) searchResults.innerHTML = fresh.innerHTML;
      } catch (_) { /* noop */ }
    }, 220);
  });

  // ---------- Filter drawer ----------
  const filterDrawer = $('#FilterDrawer');
  $$('[data-open-filter]').forEach(btn => on(btn, 'click', () => openDrawer(filterDrawer)));
  $$('[data-close-filter]').forEach(btn => on(btn, 'click', closeAll));

  // ---------- Hotspots (lookbook) ----------
  $$('.hotspot').forEach(hs => {
    on(hs, 'click', e => {
      e.stopPropagation();
      const wasOpen = hs.classList.contains('is-open');
      $$('.hotspot').forEach(h => h.classList.remove('is-open'));
      if (!wasOpen) hs.classList.add('is-open');
    });
  });
  document.addEventListener('click', () => $$('.hotspot').forEach(h => h.classList.remove('is-open')));

  // ---------- Scroll reveal ----------
  if ('IntersectionObserver' in window && document.body.dataset.animateScroll !== 'false') {
    const io = new IntersectionObserver(entries => {
      entries.forEach(en => {
        if (en.isIntersecting) {
          en.target.classList.add('is-visible');
          io.unobserve(en.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    $$('.reveal').forEach(el => io.observe(el));
  } else {
    $$('.reveal').forEach(el => el.classList.add('is-visible'));
  }

  // ==========================================================================
  //  PHASE 2 — Mega menu, Slideshow, Quick view, Size guide,
  //            Sticky ATC, Cart upsell, Recently viewed, AJAX filters,
  //            Variant gallery sync
  // ==========================================================================

  // ---------- Header height CSS var (for mega panel offset + overlay) ----------
  function setHeaderHeight() {
    const h = header ? header.offsetHeight : 64;
    document.documentElement.style.setProperty('--header-h', h + 'px');
  }
  setHeaderHeight();
  window.addEventListener('resize', setHeaderHeight, { passive: true });

  // ---------- Transparent header over the first section (hero / slideshow) ----------
  function syncHeaderOverlay() {
    if (!header) return;
    const allowed = header.dataset.allowOverlay !== 'false';
    const main = $('.main');
    const firstSection = main ? main.querySelector('.shopify-section') : null;
    const wantsOverlay = !!(firstSection && firstSection.querySelector('[data-header-overlay="true"]'));
    document.body.classList.toggle('header-overlay', allowed && wantsOverlay);
    setHeaderHeight();
  }
  syncHeaderOverlay();
  document.addEventListener('shopify:section:load', syncHeaderOverlay);
  document.addEventListener('shopify:section:unload', syncHeaderOverlay);
  document.addEventListener('shopify:section:reorder', syncHeaderOverlay);

  // ---------- Mega menu (mobile click + a11y) ----------
  const megaTriggers = $$('[data-mega] .mega__trigger');
  $$('[data-mega]').forEach(mega => {
    const trigger = mega.querySelector('.mega__trigger');
    if (!trigger) return;
    on(trigger, 'click', e => {
      // Desktop: let the link work. On touch / no-hover, toggle the panel.
      if (window.matchMedia('(hover: none), (max-width: 1100px)').matches) {
        e.preventDefault();
        const isOpen = mega.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    });
    // Keyboard: Down opens panel and focuses first link
    on(trigger, 'keydown', e => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        mega.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        const first = mega.querySelector('.mega__panel a');
        if (first) first.focus();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const idx = megaTriggers.indexOf(trigger);
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const next = megaTriggers[(idx + dir + megaTriggers.length) % megaTriggers.length];
        if (next) next.focus();
      }
    });
    // Escape in panel closes and returns focus to trigger
    on(mega.querySelector('.mega__panel'), 'keydown', e => {
      if (e.key === 'Escape') {
        mega.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
    });
  });
  document.addEventListener('click', e => {
    if (!e.target.closest('[data-mega]')) {
      $$('[data-mega].is-open').forEach(m => {
        m.classList.remove('is-open');
        m.querySelector('.mega__trigger')?.setAttribute('aria-expanded', 'false');
      });
    }
  });

  // ---------- Slideshow ----------
  $$('[data-slideshow]').forEach(slideshow => {
    const slides = $$('.slide', slideshow);
    if (slides.length <= 1) return;

    const dots = $$('.slideshow__dot', slideshow);
    const autoplay = slideshow.dataset.autoplay === 'true';
    const interval = (parseInt(slideshow.dataset.interval, 10) || 6) * 1000;
    let current = 0;
    let timer = null;

    function show(i) {
      current = (i + slides.length) % slides.length;
      slides.forEach((s, idx) => {
        s.classList.toggle('is-active', idx === current);
        s.setAttribute('aria-hidden', idx === current ? 'false' : 'true');
      });
      dots.forEach((d, idx) => d.classList.toggle('is-active', idx === current));
    }
    function next() { show(current + 1); }
    function prev() { show(current - 1); }
    function start() { if (autoplay) { stop(); timer = setInterval(next, interval); } }
    function stop() { if (timer) clearInterval(timer); timer = null; }

    on($('[data-slide-prev]', slideshow), 'click', () => { prev(); start(); });
    on($('[data-slide-next]', slideshow), 'click', () => { next(); start(); });
    dots.forEach((d, i) => on(d, 'click', () => { show(i); start(); }));
    on(slideshow, 'mouseenter', stop);
    on(slideshow, 'mouseleave', start);

    // Touch swipe
    let touchX = null;
    on(slideshow, 'touchstart', e => { touchX = e.touches[0].clientX; }, { passive: true });
    on(slideshow, 'touchend', e => {
      if (touchX === null) return;
      const dx = e.changedTouches[0].clientX - touchX;
      if (Math.abs(dx) > 40) { dx < 0 ? next() : prev(); start(); }
      touchX = null;
    });

    start();
  });

  // ---------- Modal helpers (quick view + size guide) ----------
  function openDialog(d) {
    if (!d) return;
    if (typeof d.showModal === 'function') d.showModal();
    else d.setAttribute('open', '');
    lockScroll(true);
  }
  function closeDialog(d) {
    if (!d) return;
    if (typeof d.close === 'function') d.close();
    else d.removeAttribute('open');
    lockScroll(false);
  }
  document.addEventListener('click', e => {
    const closeBtn = e.target.closest('[data-close-modal]');
    if (closeBtn) closeDialog(closeBtn.closest('dialog'));
  });
  $$('dialog.modal').forEach(d => {
    on(d, 'click', e => {
      const inner = d.querySelector('.modal__inner');
      if (inner && !inner.contains(e.target)) closeDialog(d);
    });
    on(d, 'close', () => lockScroll(false));
  });

  // ---------- Size guide ----------
  document.addEventListener('click', e => {
    if (e.target.closest('[data-open-size-guide]')) {
      openDialog($('#SizeGuideModal'));
    }
  });

  // ---------- Quick view ----------
  const qvModal = $('#QuickViewModal');
  const qvTarget = qvModal ? qvModal.querySelector('[data-quick-view-target]') : null;
  const qvSkeleton = qvTarget ? qvTarget.innerHTML : '';

  function bindQuickViewForm() {
    const form = qvTarget?.querySelector('[data-product-form], #ProductForm, .product__form');
    if (!form) return;
    const variantsData = JSON.parse(qvTarget.querySelector('[data-product-options]')?.dataset.variants || '[]');

    function pickVariant() {
      const selected = $$('[data-option-input]:checked', form).map(i => i.value);
      const match = variantsData.find(v => v.options.every((opt, i) => opt === selected[i]));
      if (!match) return null;
      form.querySelector('input[name="id"]').value = match.id;
      const priceEl = qvTarget.querySelector('[data-product-price]');
      const cmpEl = qvTarget.querySelector('[data-product-compare]');
      if (priceEl) priceEl.textContent = formatMoney(match.price, document.documentElement.dataset.currency || 'EUR');
      if (cmpEl) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          cmpEl.textContent = formatMoney(match.compare_at_price, document.documentElement.dataset.currency || 'EUR');
          cmpEl.style.display = '';
        } else {
          cmpEl.style.display = 'none';
        }
      }
      const atc = form.querySelector('[data-atc]');
      if (atc) {
        if (match.available) { atc.disabled = false; atc.textContent = atc.dataset.addText; }
        else { atc.disabled = true; atc.textContent = atc.dataset.soldoutText; }
      }
      return match;
    }

    $$('[data-option-input]', form).forEach(inp => {
      on(inp, 'change', () => {
        // Update active state on labels
        const name = inp.name;
        $$('[data-option-input][name="' + name.replace(/"/g, '\\"') + '"]', form).forEach(other => {
          other.closest('label')?.classList.toggle('is-active', other.checked);
        });
        pickVariant();
      });
    });

    on(form, 'submit', async e => {
      e.preventDefault();
      const fd = new FormData(form);
      try {
        const r = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' } });
        if (!r.ok) throw new Error(await r.text());
        await refreshCartDrawer();
        closeDialog(qvModal);
        toast('Added to bag');
        $$('[data-open-cart]')[0]?.click();
      } catch (err) {
        toast('Could not add to bag');
        console.error(err);
      }
    });
  }

  // ---------- Card / Quick view HTML builders (from /products/{handle}.js JSON) ----------
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
  }

  function buildProductCardHTML(p) {
    const primary = p.featured_image || (p.images && p.images[0]) || '';
    const compare = p.compare_at_price || 0;
    const onSale = compare > p.price;
    const currency = document.documentElement.dataset.currency || 'EUR';
    const priceHtml = onSale
      ? `<span class="price--sale">${formatMoney(p.price, currency)}</span> <span class="price--compare">${formatMoney(compare, currency)}</span>`
      : (p.price_min != null && p.price_min !== p.price_max
          ? `From ${formatMoney(p.price_min, currency)}`
          : formatMoney(p.price, currency));
    let badge = '';
    if (onSale) {
      const pct = Math.round((compare - p.price) * 100 / compare);
      badge = `<span class="card__badge card__badge--sale">−${pct}%</span>`;
    } else if (p.tags && (p.tags.includes('preorder') || p.tags.includes('pre-order'))) {
      badge = `<span class="card__badge card__badge--preorder">Pre-order</span>`;
    } else if (p.tags && p.tags.includes('new')) {
      badge = `<span class="card__badge card__badge--new">New</span>`;
    } else if (p.available === false) {
      badge = `<span class="card__badge">Sold out</span>`;
    }
    return `
      <article class="card reveal is-visible">
        <a href="${p.url}" class="card__media" aria-label="${escapeHtml(p.title)}">
          ${primary ? `<img src="${primary}" class="card__media-primary" loading="lazy" alt="${escapeHtml(p.title)}">` : ''}
          ${badge}
        </a>
        <h3 class="card__title"><a href="${p.url}">${escapeHtml(p.title)}</a></h3>
        <div class="card__price">${priceHtml}</div>
      </article>
    `;
  }

  function buildQuickViewHTML(p) {
    const primary = p.featured_image || (p.images && p.images[0]) || '';
    const variant = (p.variants || []).find(v => v.available) || (p.variants && p.variants[0]) || {};
    const currency = document.documentElement.dataset.currency || 'EUR';
    const optionsHtml = (p.options || []).map((optName, optIdx) => {
      const optValues = [...new Set((p.variants || []).map(v => v.options[optIdx]))];
      const colorWords = ['Color','Colour','Couleur','Farbe','Colore'];
      const isColor = colorWords.some(w => optName.includes(w));
      return `
        <div class="product__option">
          <div class="product__option-label"><span>${escapeHtml(optName)}</span><strong>${escapeHtml(variant.options ? variant.options[optIdx] : '')}</strong></div>
          <div class="${isColor ? 'opt-color' : 'opt-size'}" role="radiogroup" aria-label="${escapeHtml(optName)}">
            ${optValues.map(v => {
              const isActive = variant.options && variant.options[optIdx] === v;
              return `<label class="${isColor ? 'opt-color__btn' : 'opt-size__btn'}${isActive ? ' is-active' : ''}" title="${escapeHtml(v)}">
                <input type="radio" name="options[${escapeHtml(optName)}]" value="${escapeHtml(v)}" data-option-input${isActive ? ' checked' : ''}>
                ${isColor ? `<span class="visually-hidden">${escapeHtml(v)}</span>` : escapeHtml(v)}
              </label>`;
            }).join('')}
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="quick-view">
        <a href="${p.url}" class="quick-view__media">
          ${primary ? `<img src="${primary}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
        </a>
        <div class="quick-view__info">
          ${p.vendor ? `<div class="eyebrow">${escapeHtml(p.vendor)}</div>` : ''}
          <h2 class="quick-view__title"><a href="${p.url}">${escapeHtml(p.title)}</a></h2>
          <div class="product__price">
            <span data-product-price>${formatMoney(variant.price, currency)}</span>
            ${variant.compare_at_price > variant.price ? `<span class="price--compare" data-product-compare>${formatMoney(variant.compare_at_price, currency)}</span>` : ''}
          </div>
          ${p.description ? `<p class="quick-view__desc muted">${escapeHtml(p.description.replace(/<[^>]+>/g, '').slice(0, 200))}</p>` : ''}
          <form action="/cart/add" method="post" data-product-form>
            <input type="hidden" name="id" value="${variant.id || ''}">
            <div data-product-options data-variants='${JSON.stringify(p.variants || []).replace(/'/g, '&#39;')}'>
              ${optionsHtml}
            </div>
            <button type="submit" class="btn btn--block" data-atc data-add-text="Add to bag" data-soldout-text="Sold out"${variant.available ? '' : ' disabled'}>${variant.available ? 'Add to bag' : 'Sold out'}</button>
          </form>
          <a href="${p.url}" class="link mt-3" style="display:inline-block">View full details →</a>
        </div>
      </div>
    `;
  }

  document.addEventListener('click', async e => {
    const trigger = e.target.closest('[data-quick-view-trigger]');
    if (!trigger || !qvModal) return;
    e.preventDefault();
    const handle = trigger.dataset.productHandle;
    if (!handle) return;
    if (qvTarget) qvTarget.innerHTML = qvSkeleton;
    openDialog(qvModal);
    try {
      const r = await fetch('/products/' + handle + '.js');
      if (!r.ok) throw new Error('not found');
      const product = await r.json();
      if (qvTarget) {
        qvTarget.innerHTML = buildQuickViewHTML(product);
        bindQuickViewForm();
      }
    } catch (err) {
      console.error(err);
      if (qvTarget) qvTarget.innerHTML = '<p style="padding:2rem;text-align:center">Could not load product.</p>';
    }
  });

  // ---------- Sticky ATC (mobile) ----------
  const stickyAtc = $('[data-sticky-atc]');
  const productForm = $('[data-product-form], #ProductForm, .product__form');
  if (stickyAtc && productForm && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(entries => {
      entries.forEach(en => {
        const rect = en.target.getBoundingClientRect();
        const isOut = !en.isIntersecting && rect.top < 0;
        stickyAtc.classList.toggle('is-visible', isOut);
        stickyAtc.setAttribute('aria-hidden', isOut ? 'false' : 'true');
      });
    }, { threshold: 0 });
    io.observe(productForm);

    on($('[data-sticky-atc-trigger]', stickyAtc), 'click', () => {
      productForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }

  // ---------- Cart upsell ----------
  async function loadCartUpsell() {
    const el = $('[data-cart-upsell]');
    if (!el) return;
    const pid = el.dataset.productId;
    if (!pid) return;
    const list = el.querySelector('[data-cart-upsell-list]');
    try {
      const r = await fetch('/recommendations/products.json?product_id=' + pid + '&limit=2&intent=related');
      const data = await r.json();
      const products = (data.products || []).filter(p => p.available !== false);
      if (!products.length) { el.style.display = 'none'; return; }
      list.innerHTML = products.slice(0, 2).map(p => {
        const img = p.featured_image || (p.images && p.images[0]) || '';
        const firstAvailableVariant = (p.variants || []).find(v => v.available) || (p.variants || [])[0];
        const price = firstAvailableVariant ? formatMoney(firstAvailableVariant.price, document.documentElement.dataset.currency || 'EUR') : '';
        return `
          <div class="cart-upsell__item">
            <a href="${p.url}"><img src="${img}" alt="${p.title}" loading="lazy"></a>
            <div>
              <div class="cart-upsell__title"><a href="${p.url}">${p.title}</a></div>
              <div class="cart-upsell__price">${price}</div>
            </div>
            <button type="button" class="cart-upsell__add" data-upsell-add data-variant-id="${firstAvailableVariant ? firstAvailableVariant.id : ''}">Add</button>
          </div>`;
      }).join('');

      $$('[data-upsell-add]', el).forEach(btn => {
        on(btn, 'click', async () => {
          const id = btn.dataset.variantId;
          if (!id) return;
          btn.disabled = true; btn.textContent = '…';
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
            });
            await refreshCartDrawer();
            toast('Added to bag');
          } catch (e) {
            toast('Could not add');
          }
          btn.disabled = false; btn.textContent = 'Add';
        });
      });
    } catch (e) {
      el.style.display = 'none';
    }
  }
  // Run on initial load + after cart updates
  loadCartUpsell();
  document.addEventListener('cart:updated', loadCartUpsell);

  // ---------- Recently viewed ----------
  const RV_KEY = 'maison_recently_viewed';
  function readRV() {
    try { return JSON.parse(localStorage.getItem(RV_KEY) || '[]'); }
    catch (_) { return []; }
  }
  function writeRV(arr) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(arr.slice(0, 12))); }
    catch (_) {}
  }
  // Push current product handle
  const currentHandle = document.body.dataset.productHandle;
  if (currentHandle) {
    const list = readRV().filter(h => h !== currentHandle);
    list.unshift(currentHandle);
    writeRV(list);
  }

  // Render recently viewed section
  $$('[data-recently-viewed]').forEach(async section => {
    const limit = parseInt(section.dataset.limit, 10) || 4;
    const grid = section.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;
    const handles = readRV().filter(h => h !== currentHandle).slice(0, limit);
    if (!handles.length) return;
    try {
      const products = await Promise.all(handles.map(h =>
        fetch('/products/' + h + '.js').then(r => r.ok ? r.json() : null)
      ));
      const cards = products.filter(Boolean).map(buildProductCardHTML);
      if (!cards.length) return;
      grid.innerHTML = cards.join('');
      section.style.display = '';
    } catch (_) { /* silent */ }
  });

  // ---------- AJAX collection filters ----------
  const facetForm = $('#FacetFiltersForm');
  if (facetForm) {
    const collectionGrid = $('.grid.grid--4') || $('.grid');
    const toolbar = $('.toolbar');

    async function applyFilters(url) {
      try {
        const r = await fetch(url + (url.includes('?') ? '&' : '?') + 'section_id=main-collection');
        const html = await r.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = parsed.querySelector('.grid.grid--4') || parsed.querySelector('.grid');
        const newToolbar = parsed.querySelector('.toolbar');
        if (newGrid && collectionGrid) collectionGrid.innerHTML = newGrid.innerHTML;
        if (newToolbar && toolbar) toolbar.innerHTML = newToolbar.innerHTML;
        window.history.pushState({}, '', url);
      } catch (e) { console.error('Filter failed', e); }
    }

    on(facetForm, 'submit', e => {
      e.preventDefault();
      const fd = new FormData(facetForm);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v) params.append(k, v);
      applyFilters(facetForm.getAttribute('action') + '?' + params.toString());
      // Close drawer
      $('#FilterDrawer')?.classList.remove('is-open');
      lockScroll(false);
    });

    // Sort dropdown
    const sortSel = $('[name="sort_by"]');
    if (sortSel) {
      on(sortSel, 'change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSel.value);
        applyFilters(url.pathname + url.search);
      });
    }
  }

  // ---------- Variant gallery sync / media grouping ----------
  const productOptions = $('[data-product-options]');
  const gallery = $('.product__gallery');
  if (productOptions && gallery) {
    const variantsRaw = productOptions.dataset.variants;
    let variants = [];
    try { variants = JSON.parse(variantsRaw); } catch (_) {}

    // 1-based index of the option used to group media (0 = grouping disabled)
    const groupIndex = parseInt(gallery.dataset.mediaGroupIndex || '0', 10) || 0;
    const mediaItems = $$('.product__media-item', gallery);
    const firstImg = gallery.querySelector('img');

    const selectedOptions = () =>
      $$('[data-option-input]:checked', productOptions).map(i => i.value);

    // Show only the media attached to the selected option value. Media with an
    // empty group are shared across every value and stay visible.
    function applyMediaGroup() {
      if (!groupIndex || !mediaItems.length) return false;
      const value = (selectedOptions()[groupIndex - 1] || '').trim().toLowerCase();
      if (!value) return false;

      let visible = 0;
      mediaItems.forEach(item => {
        const group = (item.dataset.mediaGroup || '').trim().toLowerCase();
        const show = !group || group === value;
        item.classList.toggle('is-hidden-group', !show);
        if (show) visible++;
      });

      // Safety net: never leave the gallery empty because of a bad setup.
      if (!visible) {
        mediaItems.forEach(item => item.classList.remove('is-hidden-group'));
        return false;
      }
      return true;
    }

    // Fallback for products without grouping: swap the lead image only.
    function syncVariantImage() {
      const selected = selectedOptions();
      const match = variants.find(v => v.options.every((opt, i) => opt === selected[i]));
      if (match && match.featured_image && firstImg) {
        const src = match.featured_image.src || match.featured_image;
        if (src && firstImg.src.indexOf(src.split('?')[0]) === -1) {
          firstImg.src = src.includes('?') ? src + '&width=1400' : src + '?width=1400';
          firstImg.removeAttribute('srcset');
        }
      }
    }

    function onOptionChange() {
      if (!applyMediaGroup()) syncVariantImage();
    }

    $$('[data-option-input]', productOptions).forEach(inp => on(inp, 'change', onOptionChange));
    applyMediaGroup();
  }

  // ==========================================================================
  //  PHASE 3 — Wishlist, View toggle, Reading progress, Share, Parallax
  // ==========================================================================

  // ---------- Wishlist (localStorage) ----------
  const WL_KEY = 'maison_wishlist';
  function readWishlist() {
    try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); }
    catch (_) { return []; }
  }
  function writeWishlist(arr) {
    try { localStorage.setItem(WL_KEY, JSON.stringify(arr.slice(0, 200))); }
    catch (_) {}
  }
  function updateWishlistCount() {
    const count = readWishlist().length;
    $$('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }
  function syncWishlistButtons() {
    const list = readWishlist();
    $$('[data-wishlist-toggle]').forEach(btn => {
      const h = btn.dataset.productHandle;
      btn.setAttribute('aria-pressed', list.includes(h) ? 'true' : 'false');
    });
  }
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    let list = readWishlist();
    if (list.includes(handle)) {
      list = list.filter(h => h !== handle);
      btn.setAttribute('aria-pressed', 'false');
      toast('Removed from wishlist');
    } else {
      list.unshift(handle);
      btn.setAttribute('aria-pressed', 'true');
      toast('Added to wishlist');
    }
    writeWishlist(list);
    updateWishlistCount();
    syncWishlistButtons();
    // If we're on the wishlist page, re-render
    if ($('[data-wishlist-grid]')) renderWishlistPage();
  });
  updateWishlistCount();
  syncWishlistButtons();

  // Wishlist page rendering
  async function renderWishlistPage() {
    const grid = $('[data-wishlist-grid]');
    const empty = $('[data-wishlist-empty]');
    if (!grid) return;
    const handles = readWishlist();
    if (!handles.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    try {
      const products = await Promise.all(
        handles.map(h => fetch('/products/' + h + '.js').then(r => r.ok ? r.json() : null))
      );
      grid.innerHTML = products.filter(Boolean).map(buildProductCardHTML).join('');
      syncWishlistButtons();
    } catch (_) { /* silent */ }
  }
  if ($('[data-wishlist-grid]')) renderWishlistPage();

  // ---------- Collection view toggle ----------
  const viewToggle = $('[data-view-toggle]');
  const collectionGrid = $('[data-collection-grid]');
  if (viewToggle && collectionGrid) {
    // Restore saved preference
    try {
      const saved = localStorage.getItem('maison_view_cols');
      if (saved) {
        collectionGrid.setAttribute('data-cols', saved);
        $$('.view-toggle__btn', viewToggle).forEach(b => b.classList.toggle('is-active', b.dataset.cols === saved));
      }
    } catch (_) {}

    $$('.view-toggle__btn', viewToggle).forEach(btn => {
      on(btn, 'click', () => {
        const cols = btn.dataset.cols;
        collectionGrid.setAttribute('data-cols', cols);
        $$('.view-toggle__btn', viewToggle).forEach(b => b.classList.toggle('is-active', b === btn));
        try { localStorage.setItem('maison_view_cols', cols); } catch (_) {}
      });
    });
  }

  // ---------- Reading progress (article page) ----------
  const progress = $('[data-reading-progress]');
  if (progress) {
    const bar = progress.querySelector('.reading-progress__bar');
    const article = $('.article-body');
    if (article && bar) {
      function updateProgress() {
        const rect = article.getBoundingClientRect();
        const total = article.offsetHeight - window.innerHeight;
        const scrolled = Math.max(0, -rect.top);
        const pct = total > 0 ? Math.min(100, (scrolled / total) * 100) : 0;
        bar.style.width = pct + '%';
      }
      updateProgress();
      window.addEventListener('scroll', updateProgress, { passive: true });
      window.addEventListener('resize', updateProgress, { passive: true });
    }
  }

  // ---------- Article share — copy link ----------
  $$('[data-copy-link]').forEach(btn => {
    on(btn, 'click', async () => {
      try {
        await navigator.clipboard.writeText(window.location.href);
        const orig = btn.textContent;
        btn.textContent = '✓';
        setTimeout(() => { btn.textContent = orig; }, 1500);
      } catch (_) {
        // Fallback
        const t = document.createElement('textarea');
        t.value = window.location.href;
        document.body.appendChild(t);
        t.select(); document.execCommand('copy');
        document.body.removeChild(t);
        toast('Link copied');
      }
    });
  });

  // ---------- Parallax for editorial banners ----------
  if ('IntersectionObserver' in window) {
    $$('[data-parallax]').forEach(section => {
      const img = section.querySelector('.editorial__media img');
      if (!img) return;
      let visible = false;
      const io = new IntersectionObserver(entries => {
        entries.forEach(en => { visible = en.isIntersecting; });
      }, { threshold: 0 });
      io.observe(section);

      function update() {
        if (!visible) { requestAnimationFrame(update); return; }
        const rect = section.getBoundingClientRect();
        const range = window.innerHeight + rect.height;
        const pos = window.innerHeight - rect.top;
        const pct = pos / range; // 0 to 1
        const y = (pct - 0.5) * 60; // -30px to +30px
        section.style.setProperty('--parallax-y', y + 'px');
        requestAnimationFrame(update);
      }
      requestAnimationFrame(update);
    });
  }

  // ==========================================================================
  //  PHASE 4 — Notify-me toggle, product rail navigation
  // ==========================================================================

  // ---------- Notify me panel toggle ----------
  document.addEventListener('click', e => {
    const trigger = e.target.closest('[data-notify-trigger]');
    if (!trigger) return;
    const wrap = trigger.closest('[data-notify-me]');
    if (!wrap) return;
    const panel = wrap.querySelector('.notify-me__panel');
    if (!panel) return;
    panel.hidden = !panel.hidden;
    trigger.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
  });

  // ---------- Product rail navigation (featured-collection carousel) ----------
  $$('[data-rail]').forEach(rail => {
    const section = rail.closest('section');
    if (!section) return;
    const prev = section.querySelector('[data-rail-prev]');
    const next = section.querySelector('[data-rail-next]');

    function step() {
      const first = rail.querySelector('.product-rail__item, .complete-look__item, > *');
      return first ? (first.getBoundingClientRect().width + 24) : 280;
    }
    function updateButtons() {
      const max = rail.scrollWidth - rail.clientWidth - 4;
      if (prev) prev.disabled = rail.scrollLeft <= 0;
      if (next) next.disabled = rail.scrollLeft >= max;
    }
    if (prev) on(prev, 'click', () => { rail.scrollBy({ left: -step() * 2, behavior: 'smooth' }); });
    if (next) on(next, 'click', () => { rail.scrollBy({ left: step() * 2, behavior: 'smooth' }); });
    on(rail, 'scroll', updateButtons);
    window.addEventListener('resize', updateButtons, { passive: true });
    updateButtons();
  });

  // ==========================================================================
  //  PHASE 5 — Image zoom, Quick-add, Gift wrap, Estimated delivery,
  //            Card swatch hover image swap
  // ==========================================================================

  // ---------- Image zoom modal ----------
  const zoomModal = $('#ImageZoomModal');
  if (zoomModal) {
    const zoomImg = $('[data-zoom-img]', zoomModal);
    const zoomCounter = $('[data-zoom-counter]', zoomModal);
    const zoomPrev = $('[data-zoom-prev]', zoomModal);
    const zoomNext = $('[data-zoom-next]', zoomModal);
    let zoomImages = [];
    let zoomIndex = 0;

    function setZoomImage(idx) {
      if (!zoomImages.length) return;
      zoomIndex = (idx + zoomImages.length) % zoomImages.length;
      zoomImg.src = zoomImages[zoomIndex];
      zoomImg.classList.remove('is-zoomed');
      zoomCounter.textContent = (zoomIndex + 1) + ' / ' + zoomImages.length;
    }

    document.addEventListener('click', e => {
      const img = e.target.closest('.product__gallery img');
      if (!img) return;
      // Gather all images in the gallery
      const gallery = img.closest('.product__gallery');
      const allImgs = $$('img', gallery).filter(
        i => !i.closest('.product__media-item.is-hidden-group')
      );
      zoomImages = allImgs.map(i => {
        const src = i.currentSrc || i.src;
        return src.replace(/width=\d+/, 'width=2000');
      });
      zoomIndex = allImgs.indexOf(img);
      setZoomImage(zoomIndex);
      openDialog(zoomModal);
    });

    on(zoomPrev, 'click', () => setZoomImage(zoomIndex - 1));
    on(zoomNext, 'click', () => setZoomImage(zoomIndex + 1));
    on(zoomImg, 'click', () => zoomImg.classList.toggle('is-zoomed'));

    // Keyboard nav
    on(zoomModal, 'keydown', e => {
      if (e.key === 'ArrowLeft') setZoomImage(zoomIndex - 1);
      if (e.key === 'ArrowRight') setZoomImage(zoomIndex + 1);
    });
  }

  // ---------- Quick-add from card ----------
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    const originalHTML = btn.innerHTML;
    btn.innerHTML = '…';
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!r.ok) throw new Error(await r.text());
      btn.classList.add('is-added');
      btn.innerHTML = '✓';
      await refreshCartDrawer();
      const cart = await fetchCart();
      updateCartCount(cart.item_count);
      toast('Added to bag');
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.innerHTML = originalHTML;
        btn.disabled = false;
      }, 1400);
    } catch (err) {
      btn.innerHTML = originalHTML;
      btn.disabled = false;
      toast('Could not add');
    }
  });

  // ---------- Gift wrap toggle ----------
  document.addEventListener('change', async e => {
    const label = e.target.closest('[data-gift-wrap]');
    if (!label || e.target.type !== 'checkbox') return;
    const variantId = label.dataset.variantId;
    if (!variantId) return;
    const checked = e.target.checked;
    try {
      if (checked) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
      } else {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 0 })
        });
      }
      await refreshCartDrawer();
    } catch (err) {
      e.target.checked = !checked; // revert
      toast('Could not update');
    }
  });

  // ---------- Estimated delivery ----------
  function renderEstimatedDelivery() {
    const el = $('[data-estimated-delivery]');
    if (!el) return;
    const min = parseInt(el.dataset.min, 10) || 3;
    const max = parseInt(el.dataset.max, 10) || 5;
    const out = el.querySelector('[data-delivery-range]');
    if (!out) return;

    function addBusinessDays(date, days) {
      const d = new Date(date);
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) added++;
      }
      return d;
    }
    const lang = document.documentElement.lang || 'en';
    const fmt = new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' });
    const today = new Date();
    const minDate = addBusinessDays(today, min);
    const maxDate = addBusinessDays(today, max);
    out.textContent = fmt.format(minDate) + ' – ' + fmt.format(maxDate);
  }
  renderEstimatedDelivery();
  document.addEventListener('cart:updated', renderEstimatedDelivery);

  // ---------- Card swatch hover (image swap) ----------
  $$('[data-card-swatches]').forEach(swatchGroup => {
    const card = swatchGroup.closest('.card');
    if (!card) return;
    const primary = card.querySelector('.card__media-primary');
    if (!primary) return;
    const originalSrc = primary.src;
    const originalSrcset = primary.srcset;

    $$('[data-card-swatch]', swatchGroup).forEach(btn => {
      const variantImg = btn.dataset.variantImage;
      if (!variantImg) return;

      on(btn, 'mouseenter', () => {
        primary.src = variantImg;
        primary.removeAttribute('srcset');
        $$('.card__swatch-btn', swatchGroup).forEach(b => b.classList.toggle('is-active', b === btn));
      });

      on(btn, 'click', e => {
        e.preventDefault();
        e.stopPropagation();
        const link = card.querySelector('.card__media');
        if (link && btn.dataset.variantId) {
          const baseUrl = link.getAttribute('href').split('?')[0];
          link.setAttribute('href', baseUrl + '?variant=' + btn.dataset.variantId);
        }
        primary.src = variantImg;
        $$('.card__swatch-btn', swatchGroup).forEach(b => b.classList.toggle('is-active', b === btn));
      });
    });

    on(swatchGroup, 'mouseleave', () => {
      if (!swatchGroup.querySelector('.card__swatch-btn.is-active')) {
        primary.src = originalSrc;
        if (originalSrcset) primary.srcset = originalSrcset;
      }
    });
  });

  // ==========================================================================
  //  PHASE 6 — Excellence: Back-to-top, Save-for-later, Variant inventory,
  //            Smooth scroll, Focus management
  // ==========================================================================

  // ---------- Back-to-top button ----------
  const btt = $('[data-back-to-top]');
  if (btt) {
    function updateBtt() {
      const show = window.scrollY > window.innerHeight;
      btt.classList.toggle('is-visible', show);
      btt.hidden = !show;
    }
    window.addEventListener('scroll', updateBtt, { passive: true });
    updateBtt();
    on(btt, 'click', () => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });
  }

  // ---------- Smooth scroll for in-page anchors ----------
  document.addEventListener('click', e => {
    const a = e.target.closest('a[href^="#"]:not([href="#"])');
    if (!a) return;
    const target = $(a.getAttribute('href'));
    if (!target) return;
    e.preventDefault();
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    // Set focus to target for a11y (with -1 tabindex to allow focus)
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  // ---------- Save for later (move cart item to wishlist) ----------
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-save-for-later]');
    if (!btn) return;
    const handle = btn.dataset.productHandle;
    const line = +btn.dataset.line;
    if (!handle || !line) return;
    btn.disabled = true;
    try {
      // Add to wishlist (reuse WL helpers)
      let list = readWishlist();
      if (!list.includes(handle)) {
        list.unshift(handle);
        writeWishlist(list);
        updateWishlistCount();
        syncWishlistButtons();
      }
      // Remove from cart
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line, quantity: 0 })
      });
      await refreshCartDrawer();
      const cart = await fetchCart();
      updateCartCount(cart.item_count);
      toast('Saved for later');
    } catch (err) {
      console.error(err);
      toast('Could not save');
      btn.disabled = false;
    }
  });

  // ---------- Per-variant inventory display on size buttons ----------
  function applyVariantInventoryHints() {
    const dataEl = $('[data-variant-inventory]');
    if (!dataEl) return;
    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (_) { return; }

    const sizeButtons = $$('.opt-size__btn');
    if (!sizeButtons.length) return;

    // Build map: size value → aggregated stock info (per current selected color, if applicable)
    const productOptions = $('[data-product-options]');
    if (!productOptions) return;

    function getSelectedNonSizeValues() {
      const selected = {};
      $$('input[data-option-input]:checked', productOptions).forEach(inp => {
        const name = inp.name.replace('options[', '').replace(']', '');
        selected[name] = inp.value;
      });
      return selected;
    }

    function detectSizeOptionIndex() {
      const sizeWords = ['Size', 'Taille', 'Größe', 'Talla', 'Taglia'];
      const labels = $$('.product__option-label span', productOptions).map(s => s.textContent.trim());
      for (let i = 0; i < labels.length; i++) {
        if (sizeWords.some(w => labels[i].includes(w))) return i;
      }
      return -1;
    }

    const sizeOptIdx = detectSizeOptionIndex();
    if (sizeOptIdx === -1) return;

    function updateHints() {
      const selected = getSelectedNonSizeValues();
      const selectedArr = $$('input[data-option-input]:checked', productOptions).map(i => i.value);

      sizeButtons.forEach(label => {
        const input = label.querySelector('input');
        if (!input) return;
        const sizeValue = input.value;
        // Find matching variant by replacing the size option with this size
        const candidateOptions = selectedArr.slice();
        candidateOptions[sizeOptIdx] = sizeValue;
        const match = variants.find(v => v.options.every((o, i) => o === candidateOptions[i]));

        // Reset
        label.removeAttribute('data-stock');
        label.removeAttribute('data-stock-label');

        if (!match) return;
        if (!match.available) {
          label.setAttribute('data-stock', 'out');
        } else if (match.inventory_management && match.inventory_policy === 'deny' && match.inventory_quantity > 0 && match.inventory_quantity <= 3) {
          label.setAttribute('data-stock', 'low');
          label.setAttribute('data-stock-label', match.inventory_quantity);
        }
      });
    }

    updateHints();
    // Re-run on any non-size option change
    $$('input[data-option-input]', productOptions).forEach(inp => {
      on(inp, 'change', updateHints);
    });
  }
  applyVariantInventoryHints();

})();
